import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2, MessageCircle, ChevronDown, ChevronUp, ArrowUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ClaudeMessage, ClaudeSession, PaginationState } from "../types";
import { CollapsibleMessageNode } from "./CollapsibleMessageNode";
import { cn } from "../utils/cn";
import { useAppStore } from "../store/useAppStore";

// Flattened message item with depth info for virtual list
interface FlattenedMessage {
  message: ClaudeMessage;
  depth: number;
  uniqueKey: string;
}

interface MessageViewerProps {
  messages: ClaudeMessage[];
  pagination: PaginationState;
  isLoading: boolean;
  selectedSession: ClaudeSession | null;
  onLoadMore: () => void;
}

// 타입 안전한 parent UUID 추출 함수
const getParentUuid = (message: ClaudeMessage): string | null | undefined => {
  const msgWithParent = message as ClaudeMessage & {
    parentUuid?: string;
    parent_uuid?: string;
  };
  return msgWithParent.parentUuid || msgWithParent.parent_uuid;
};

export const MessageViewer: React.FC<MessageViewerProps> = ({
  messages,
  pagination,
  isLoading,
  selectedSession,
  onLoadMore,
}) => {
  const { t } = useTranslation("components");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(messages.length);

  // Get message order and collapse setting from store
  const { messageOrder, setMessageOrder, collapseNonUserMessages } = useAppStore();

  // 무한 렌더링 방지를 위한 ref
  const isProcessingLoadMore = useRef(false);
  const lastPaginationCall = useRef<number>(0);

  // Track previous message count for scroll position adjustment
  const prevFlattenedLength = useRef(0);

  // 메시지 트리 구조 메모이제이션 (성능 최적화)
  const { rootMessages, uniqueMessages } = useMemo(() => {
    if (messages.length === 0) {
      return { rootMessages: [], uniqueMessages: [] };
    }

    // 중복 제거
    const uniqueMessages = Array.from(
      new Map(messages.map((msg) => [msg.uuid, msg])).values()
    );

    // 루트 메시지 찾기
    const roots: ClaudeMessage[] = [];
    uniqueMessages.forEach((msg) => {
      const parentUuid = getParentUuid(msg);
      if (!parentUuid) {
        roots.push(msg);
      }
    });

    return { rootMessages: roots, uniqueMessages };
  }, [messages]);

  // Flatten message tree for virtual list (key optimization for performance!)
  const flattenedMessages = useMemo((): FlattenedMessage[] => {
    if (messages.length === 0) return [];

    const result: FlattenedMessage[] = [];

    // Recursive flatten function with circular reference check
    const flattenTree = (
      message: ClaudeMessage,
      depth: number,
      visited: Set<string>,
      keyPrefix: string
    ) => {
      if (visited.has(message.uuid)) return;
      visited.add(message.uuid);

      const uniqueKey = keyPrefix
        ? `${keyPrefix}-${message.uuid}`
        : message.uuid;
      result.push({ message, depth, uniqueKey });

      // Find children
      const children = uniqueMessages.filter((m) => {
        const parentUuid = getParentUuid(m);
        return parentUuid === message.uuid;
      });

      children.forEach((child, index) => {
        flattenTree(child, depth + 1, new Set(visited), `${uniqueKey}-c${index}`);
      });
    };

    if (rootMessages.length > 0) {
      // Tree structure: flatten recursively
      rootMessages.forEach((root, index) => {
        flattenTree(root, 0, new Set(), `root-${index}`);
      });
    } else {
      // Flat structure: just map with depth=0
      uniqueMessages.forEach((msg, index) => {
        const uniqueKey =
          msg.uuid && msg.uuid !== "unknown-session"
            ? `${msg.uuid}-${index}`
            : `flat-${index}-${msg.timestamp}-${msg.type}`;
        result.push({ message: msg, depth: 0, uniqueKey });
      });
    }

    return result;
  }, [messages, rootMessages, uniqueMessages]);

  // Virtual list setup - key to performance!
  const rowVirtualizer = useVirtualizer({
    count: flattenedMessages.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 120, // Estimated row height (will be measured)
    overscan: 3, // Render 3 extra items above/below visible area
  });

  // Message change detection - handle scroll position based on order (for virtual list)
  useEffect(() => {
    const prevLength = prevMessagesLength.current;
    const currentLength = messages.length;

    // Only execute when message length changed
    if (prevLength !== currentLength) {
      // When messages are added via "load more"
      if (prevLength > 0 && currentLength > prevLength && isProcessingLoadMore.current) {
        if (messageOrder === "desc") {
          // desc mode: new messages are prepended at the beginning
          // Calculate how many new items were added
          const newItemsCount = flattenedMessages.length - prevFlattenedLength.current;

          if (newItemsCount > 0) {
            // Use requestAnimationFrame to ensure virtual list has updated
            requestAnimationFrame(() => {
              // Scroll to the item that was previously at the top
              rowVirtualizer.scrollToIndex(newItemsCount, { align: "start" });
              isProcessingLoadMore.current = false;
            });
          } else {
            isProcessingLoadMore.current = false;
          }
        } else {
          // asc mode: new messages appended, no scroll adjustment needed
          isProcessingLoadMore.current = false;
        }
      }

      prevMessagesLength.current = currentLength;
      prevFlattenedLength.current = flattenedMessages.length;
    }
  }, [messages.length, messageOrder, flattenedMessages.length, rowVirtualizer]);

  // 이전 세션 ID를 추적
  const prevSessionIdRef = useRef<string | null>(null);

  // 맨 아래로 스크롤하는 함수
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      const element = scrollContainerRef.current;
      // 여러 번 시도하여 확실히 맨 아래로 이동
      const attemptScroll = (attempts = 0) => {
        element.scrollTop = element.scrollHeight;
        if (
          attempts < 3 &&
          element.scrollTop < element.scrollHeight - element.clientHeight - 10
        ) {
          setTimeout(() => attemptScroll(attempts + 1), 50);
        }
      };
      attemptScroll();
    }
  }, []);

  // 맨 위로 스크롤하는 함수 (order=asc에서 사용)
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // When new session is selected, scroll based on order
  useEffect(() => {
    // Only execute when session actually changed and messages are loaded
    if (
      selectedSession &&
      prevSessionIdRef.current !== selectedSession.session_id &&
      messages.length > 0 &&
      !isLoading
    ) {
      // Update previous session ID
      prevSessionIdRef.current = selectedSession.session_id;

      // Scroll based on order after DOM update
      setTimeout(() => {
        if (messageOrder === "asc") {
          scrollToTop();
        } else {
          scrollToBottom();
        }
      }, 100);
    }
  }, [selectedSession, messages.length, isLoading, scrollToTop, scrollToBottom, messageOrder]);

  // When pagination resets (new session or refresh), scroll based on order
  useEffect(() => {
    if (pagination.currentOffset === 0 && messages.length > 0 && !isLoading) {
      setTimeout(() => {
        if (messageOrder === "asc") {
          scrollToTop();
        } else {
          scrollToBottom();
        }
      }, 50);
    }
  }, [pagination.currentOffset, messages.length, isLoading, scrollToTop, scrollToBottom, messageOrder]);

  // 더보기 버튼 최적화 (중복 호출 방지)
  const handleLoadMoreWithScroll = useCallback(() => {
    const now = Date.now();

    // 중복 클릭 방지 (500ms 내 중복 호출 차단)
    if (
      !pagination.hasMore ||
      pagination.isLoadingMore ||
      isLoading ||
      isProcessingLoadMore.current ||
      now - lastPaginationCall.current < 500
    ) {
      return;
    }

    lastPaginationCall.current = now;
    isProcessingLoadMore.current = true;

    try {
      onLoadMore();
    } catch (error) {
      console.error("Load more execution error:", error);
      isProcessingLoadMore.current = false;
    }
  }, [pagination.hasMore, pagination.isLoadingMore, isLoading, onLoadMore]);

  // 스크롤 위치 상태 추가
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // 스크롤 이벤트: 플로팅 버튼 표시/숨김 + 자동 로드 (성능 최적화 후 재활성화)
  useEffect(() => {
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        try {
          if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } =
              scrollContainerRef.current;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            const isNearBottom = distanceFromBottom < 150;
            const isNearTop = scrollTop < 150;
            setShowScrollToBottom(!isNearBottom && messages.length > 5);
            setShowScrollToTop(!isNearTop && messages.length > 5);

            // Auto-load more messages when scrolling to edge
            // asc mode: load more at bottom, desc mode: load more at top
            if (
              pagination.hasMore &&
              !pagination.isLoadingMore &&
              !isLoading &&
              !isProcessingLoadMore.current
            ) {
              const shouldAutoLoad =
                (messageOrder === "asc" && isNearBottom) ||
                (messageOrder === "desc" && isNearTop);

              if (shouldAutoLoad) {
                handleLoadMoreWithScroll();
              }
            }
          }
        } catch (error) {
          console.error("Scroll handler error:", error);
        }
        throttleTimer = null;
      }, 100);
    };

    const scrollElement = scrollContainerRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();

      return () => {
        if (throttleTimer) {
          clearTimeout(throttleTimer);
        }
        scrollElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [messages.length, pagination.hasMore, pagination.isLoadingMore, isLoading, messageOrder, handleLoadMoreWithScroll]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="flex items-center space-x-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t("messageViewer.loadingMessages")}</span>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 h-full">
        <div className="mb-4">
          <MessageCircle className="w-16 h-16 mx-auto text-gray-400" />
        </div>
        <h3 className="text-lg font-medium mb-2">
          {t("messageViewer.noMessages")}
        </h3>
        <p className="text-sm text-center whitespace-pre-line">
          {t("messageViewer.noMessagesDescription")}
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex-1 h-full">
      <div
        ref={scrollContainerRef}
        className="flex-1 h-full overflow-y-auto scrollbar-thin"
        style={{ scrollBehavior: "auto" }} // smooth 대신 auto로 즉각적인 스크롤
      >
        {/* 디버깅 정보 */}
        {import.meta.env.DEV && (
          <div className="bg-yellow-50 p-2 text-xs text-yellow-800 border-b space-y-1">
            <div>
              {t("messageViewer.debugInfo.messages", {
                current: messages.length,
                total: pagination.totalCount,
              })}{" "}
              |{" "}
              {t("messageViewer.debugInfo.offset", {
                offset: pagination.currentOffset,
              })}{" "}
              |{" "}
              {t("messageViewer.debugInfo.hasMore", {
                hasMore: pagination.hasMore ? "O" : "X",
              })}{" "}
              |{" "}
              {t("messageViewer.debugInfo.loading", {
                loading: pagination.isLoadingMore ? "O" : "X",
              })}
            </div>
            <div>
              {t("messageViewer.debugInfo.session", {
                sessionId: selectedSession?.session_id?.slice(-8),
              })}{" "}
              |{" "}
              {t("messageViewer.debugInfo.file", {
                fileName: selectedSession?.file_path
                  ?.split("/")
                  .pop()
                  ?.slice(0, 20),
              })}
            </div>
            {messages.length > 0 && (
              <div>
                {t("messageViewer.debugInfo.firstMessage", {
                  timestamp: messages[0]?.timestamp,
                })}{" "}
                |{" "}
                {t("messageViewer.debugInfo.lastMessage", {
                  timestamp: messages[messages.length - 1]?.timestamp,
                })}
              </div>
            )}
          </div>
        )}
        <div className="max-w-4xl mx-auto">
          {/* Load more button (top position for desc mode) */}
          {messageOrder === "desc" && pagination.hasMore && (
            <div className="flex items-center justify-center py-4">
              {pagination.isLoadingMore ? (
                <div className="flex items-center space-x-2 text-gray-500 py-2 px-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {t("messageViewer.loadingPreviousMessages", {
                      current: messages.length,
                      total: pagination.totalCount,
                    })}
                  </span>
                </div>
              ) : (
                <button
                  onClick={handleLoadMoreWithScroll}
                  className="flex items-center space-x-2 py-2 px-4 bg-purple-50 dark:bg-purple-900 text-purple-600 dark:text-purple-300 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-800 transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                  <span>
                    {t("messageViewer.loadPreviousMessages", {
                      count: (() => {
                        const remainingMessages =
                          pagination.totalCount - messages.length;
                        const messagesToLoad = Math.min(
                          pagination.pageSize,
                          remainingMessages
                        );
                        return messagesToLoad;
                      })(),
                      current: messages.length,
                      total: pagination.totalCount,
                    })}
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Virtual Message List - only renders visible items! */}
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = flattenedMessages[virtualRow.index];
              if (!item) return null;

              return (
                <div
                  key={item.uniqueKey}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <CollapsibleMessageNode
                    message={item.message}
                    depth={item.depth}
                    isCollapsible={collapseNonUserMessages && item.message.type !== "user"}
                    defaultExpanded={!(collapseNonUserMessages && item.message.type !== "user")}
                  />
                </div>
              );
            })}
          </div>

          {/* Load more button (bottom position for asc mode) */}
          {messageOrder === "asc" && pagination.hasMore && (
            <div className="flex items-center justify-center py-4">
              {pagination.isLoadingMore ? (
                <div className="flex items-center space-x-2 text-gray-500 py-2 px-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {t("messageViewer.loadingNextMessages", {
                      current: messages.length,
                      total: pagination.totalCount,
                    })}
                  </span>
                </div>
              ) : (
                <button
                  onClick={handleLoadMoreWithScroll}
                  className="flex items-center space-x-2 py-2 px-4 bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>
                    {t("messageViewer.loadNextMessages", {
                      count: (() => {
                        const remainingMessages =
                          pagination.totalCount - messages.length;
                        const messagesToLoad = Math.min(
                          pagination.pageSize,
                          remainingMessages
                        );
                        return messagesToLoad;
                      })(),
                      current: messages.length,
                      total: pagination.totalCount,
                    })}
                  </span>
                </button>
              )}
            </div>
          )}

          {/* 로딩 완료 메시지 (하단) */}
          {!pagination.hasMore && messages.length > 0 && (
            <div className="flex items-center justify-center py-4">
              <div className="text-gray-400 text-sm">
                {t("messageViewer.allMessagesLoaded", {
                  count: pagination.totalCount,
                })}
              </div>
            </div>
          )}
        </div>

        {/* Floating order toggle button */}
        <button
          onClick={() => setMessageOrder(messageOrder === "asc" ? "desc" : "asc")}
          className={cn(
            "fixed bottom-38 right-2 p-3 rounded-full shadow-lg transition-all duration-300 z-50",
            "bg-orange-500/50 hover:bg-orange-600 text-white",
            "hover:scale-110 focus:outline-none focus:ring-4 focus:ring-orange-300",
            "dark:bg-orange-600/50 dark:hover:bg-orange-700 dark:focus:ring-orange-800"
          )}
          title={t(messageOrder === "asc" ? "messageViewer.switchToNewestFirst" : "messageViewer.switchToOldestFirst")}
          aria-label={t(messageOrder === "asc" ? "messageViewer.switchToNewestFirst" : "messageViewer.switchToOldestFirst")}
        >
          <ArrowUpDown className="w-3 h-3" />
        </button>

        {/* Floating scroll to top button */}
        {showScrollToTop && (
          <button
            onClick={scrollToTop}
            className={cn(
              "fixed bottom-24 right-2 p-3 rounded-full shadow-lg transition-all duration-300 z-50",
              "bg-green-500/50 hover:bg-green-600 text-white",
              "hover:scale-110 focus:outline-none focus:ring-4 focus:ring-green-300",
              "dark:bg-green-600/50 dark:hover:bg-green-700 dark:focus:ring-green-800",
              showScrollToTop
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2"
            )}
            title={t("messageViewer.scrollToTop")}
            aria-label={t("messageViewer.scrollToTop")}
          >
            <ChevronUp className="w-3 h-3" />
          </button>
        )}

        {/* 플로팅 맨 아래로 버튼 */}
        {showScrollToBottom && (
          <button
            onClick={scrollToBottom}
            className={cn(
              "fixed bottom-10 right-2 p-3 rounded-full shadow-lg transition-all duration-300 z-50",
              "bg-blue-500/50 hover:bg-blue-600 text-white",
              "hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300",
              "dark:bg-blue-600/50 dark:hover:bg-blue-700 dark:focus:ring-blue-800",
              showScrollToBottom
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2"
            )}
            title={t("messageViewer.scrollToBottom")}
            aria-label={t("messageViewer.scrollToBottom")}
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};
